import {MenuItem, Stack} from '@mui/material';
import React, {ReactElement, useEffect, useRef, useState} from 'react';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import {
  ContentCopyRounded,
  ContentPasteGoRounded,
  EditNoteRounded,
  EmailRounded,
  Link,
  RefreshRounded,
  RestoreFromTrashRounded,
  GroupAddRounded, RotateRightRounded,
} from '@mui/icons-material';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import {useStateContext} from '../../hooks/useStateContext';
import {hasPermissionByPriority} from '../../core/permissions/hasPermission';
import {ROLE_PRIORITIES} from '../../core/permissions/permissions';
import {SchemaWithClient} from '../../types/SchemaWithClient';
import {Entity} from '../../types/entities/Entity';
import {toaster} from '../../services/toaster';

type Props<T extends Entity> = {
  rightClickedRow: any;
  anchorPosition: any;
  setContextMenu: () => void;
  setCurrentEditedRow: (value: any, type: any) => void;
  deleteClick: (row: any) => void;
  duplicateClick: (row: any) => void;
  restoreClick?: (row: any) => void;
  openReservations: (row: any) => void;
  openLogs: (row: any) => void;
  generateReservationClipboardData?: (row: any) => void;
  fillOptionSend?: (row: any, type: string) => void;
  openModifications?: () => void;
  schema: SchemaWithClient<T>;
  isInFullPanel?: boolean;
  openGuideOptions: (row: any) => void;
  refreshStatuses: (row: any) => void;
};

export function FloatingMenu<T extends Entity>({
                                                 anchorPosition,
                                                 rightClickedRow,
                                                 setContextMenu,
                                                 setCurrentEditedRow,
                                                 deleteClick,
                                                 duplicateClick,
                                                 restoreClick,
                                                 openReservations,
                                                 openLogs,
                                                 openModifications,
                                                 generateReservationClipboardData,
                                                 fillOptionSend,
                                                 schema,
                                                 isInFullPanel,
                                                 openGuideOptions,
                                                 refreshStatuses
                                               }: Props<T>): ReactElement {
  const [position, setPosition] = useState<any>(anchorPosition);
  const windowRef = useRef<any>(null);
  const {user} = useStateContext();

  useEffect(() => {
    const adjustPosition = () => {
      const menu = windowRef.current;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      if (menu) {
        const menuRect = menu.getBoundingClientRect();
        let {top, left} = anchorPosition;

        // Check and adjust horizontal overflow
        if (left + menuRect.width > viewportWidth) {
          left = viewportWidth - menuRect.width - 10; // Add some padding
        }
        if (left < 0) {
          left = 10; // Prevent left overflow
        }

        // Check and adjust vertical overflow
        if (top + menuRect.height > viewportHeight) {
          top = viewportHeight - menuRect.height - 10; // Add some padding
        }
        if (top < 0) {
          top = 10; // Prevent top overflow
        }

        setPosition({top, left});
      }
    };

    adjustPosition();
  }, [anchorPosition]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        windowRef.current &&
        !windowRef.current.contains(event.target as Node)
      ) {
        setContextMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setContextMenu]);

  const canDelete = hasPermissionByPriority(user.roles, ROLE_PRIORITIES[1]) || hasPermissionByPriority(user.roles, ROLE_PRIORITIES[100]) || hasPermissionByPriority(user.roles, ROLE_PRIORITIES[101]);

  return (
    <>
      <div
        ref={windowRef}
        style={{
          position: 'absolute',
          top: `${position.top}px`,
          left: `${position.left}px`,
          padding: '10px',
          borderRadius: '5px',
          backgroundColor: 'white',
          boxShadow: 'rgb(0 0 0 / 21%) 0px 1px 4px 2px',
          zIndex: 99999,
        }}
      >
        <MenuItem
          onClick={() => {
            setContextMenu();
            if (setCurrentEditedRow) {
              setCurrentEditedRow(rightClickedRow, false);
            }
          }}
        >
          <Stack direction={'row'} gap={1}>
            <EditIcon color={'primary'} /> <span>Side panel</span>
          </Stack>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setContextMenu();
            if (setCurrentEditedRow) {
              setCurrentEditedRow(rightClickedRow, true);
            }
          }}
        >
          <Stack direction={'row'} gap={1}>
            <EditNoteRounded color={'primary'} /> <span>Full screen</span>
          </Stack>
        </MenuItem>

        {!rightClickedRow?.deleted_at && (
          <>
            {canDelete && (
              <MenuItem
                onClick={() => {
                  setContextMenu();
                  deleteClick(rightClickedRow);
                }}
              >
                <Stack direction={'row'} gap={1}>
                  <DeleteIcon color={'error'} /> <span>Delete</span>
                </Stack>
              </MenuItem>
            )}

            <MenuItem
              onClick={() => {
                setContextMenu();
                duplicateClick(rightClickedRow);
              }}
            >
              <Stack direction={'row'} gap={1}>
                <ContentCopyRounded /> <span>Duplicate</span>
              </Stack>
            </MenuItem>
          </>
        )}

        {(schema?.pluralName === 'Groups' ||
          schema?.pluralName === 'Activity Reservations') &&
          !rightClickedRow?.deleted_at && (
            <>
              <MenuItem
                onClick={() => {
                  setContextMenu();

                  const sendData: any = {
                    name:
                      schema?.pluralName === 'Activity Reservations'
                        ? rightClickedRow?.group?.name
                          ? `${rightClickedRow?.group?.name} - ${rightClickedRow?.activity?.name}`
                          : `${rightClickedRow?.activity?.name}`
                        : rightClickedRow?.name,
                  };

                  if (schema?.pluralName === 'Activity Reservations') {
                    sendData.activity = rightClickedRow?.activity;
                    sendData.activity_reservation = {
                      id: rightClickedRow?.id,
                      name: rightClickedRow?.activity?.name,
                    };
                    sendData.group = rightClickedRow?.group;

                    if (rightClickedRow?.is_group) {
                      sendData.activity.is_group = 1;
                    }
                  }

                  if (schema?.pluralName === 'Groups') {
                    sendData.group = {
                      id: rightClickedRow?.id,
                      name: rightClickedRow?.name,
                    };
                  }

                  console.log(sendData);

                  openReservations(sendData);
                }}
              >
                <Stack direction={'row'} gap={1}>
                  <OpenInNewRoundedIcon /> <span>Open Reservations</span>
                </Stack>
              </MenuItem>
            </>
          )}

        {schema?.pluralName === 'Activity Reservations' &&
          !rightClickedRow?.deleted_at && (
            <>
              <MenuItem
                onClick={() => {
                  setContextMenu();
                  openGuideOptions(rightClickedRow);
                }}
              >
                <Stack direction={'row'} gap={1}>
                  <GroupAddRounded color={'info'} /> <span>Guide Options</span>
                </Stack>
              </MenuItem>
            </>
          )}

        {(schema?.pluralName === 'Activity Reservations' ||
          schema?.pluralName === 'Service Reservations' ||
          schema?.pluralName === 'Groups') && (
          <>
            {rightClickedRow?.deleted_at && (
              <MenuItem
                onClick={() => {
                  setContextMenu();
                  if (restoreClick) {
                    restoreClick(rightClickedRow);
                  }
                }}
              >
                <Stack direction={'row'} gap={1}>
                  <RestoreFromTrashRounded /> <span>Restore</span>
                </Stack>
              </MenuItem>
            )}
          </>
        )}

        {schema?.pluralName === 'Service Reservations' &&
          !rightClickedRow?.deleted_at && (
            <>
              <MenuItem
                onClick={() => {
                  setContextMenu();
                  if (generateReservationClipboardData) {
                    generateReservationClipboardData(rightClickedRow);
                  }
                }}
              >
                <Stack direction={'row'} gap={1}>
                  <ContentPasteGoRounded /> <span>Copy to clipboard</span>
                </Stack>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setContextMenu();
                  if (openModifications) {
                    openModifications();
                  }
                }}
              >
                <Stack direction={'row'} gap={1}>
                  <EmailRounded sx={{ color: '#9d8718' }} />{' '}
                  <span>Send To Provider</span>
                </Stack>
              </MenuItem>
            </>
          )}

        {schema?.pluralName === 'Activity Reservations' &&
          !rightClickedRow?.deleted_at && (
            <>
              <MenuItem
                onClick={() => {
                  setContextMenu();
                  if (fillOptionSend) {
                    fillOptionSend(rightClickedRow, 'guide');
                  }
                }}
              >
                <Stack direction={'row'} gap={1}>
                  <RefreshRounded /> <span>Fill Guides</span>
                </Stack>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setContextMenu();
                  if (fillOptionSend) {
                    fillOptionSend(rightClickedRow, 'meeting_place');
                  }
                }}
              >
                <Stack direction={'row'} gap={1}>
                  <RefreshRounded /> <span>Fill Meeting Place</span>
                </Stack>
              </MenuItem>
            </>
          )}

        {/*{schema?.pluralName === 'Groups' && (
          <>
            <MenuItem
              onClick={() => {
                setContextMenu();
                if (refreshStatuses) {
                  refreshStatuses(rightClickedRow);
                }
              }}
            >
              <Stack direction={'row'} gap={1}>
                <RotateRightRounded /> <span>Refresh Statuses (A/B/S)</span>
              </Stack>
            </MenuItem>
          </>
        )}*/}

        <MenuItem
          onClick={() => {
            setContextMenu();
            const url = `${
              window.location.origin
            }${schema.client.getAllPath.replace(
              'service-reservations-view',
              'service-reservations'
            )}/${rightClickedRow.id}`;
            navigator.clipboard
              .writeText(url)
              .then(() =>
                toaster.show({
                  message: `Successfully copied the following URL to the clipboard: ${url}`,
                  intent: 'success',
                })
              )
              .catch(() =>
                toaster.show({
                  message: `Failed to copy the following URL to the clipboard: ${url}`,
                  intent: 'danger',
                })
              );
          }}
        >
          <Stack direction={'row'} gap={1}>
            <Link /> <span>Copy URL</span>
          </Stack>
        </MenuItem>

        {(
          schema?.viewTable === 'activity_reservations' ||
          schema?.viewTable === 'service_reservations' ||
          schema?.viewTable === 'activities' ||
          schema?.viewTable === 'service_providers' ||
          schema?.viewTable === 'accommodations' ||
          schema?.viewTable === 'groups'
        ) && (
          <>
            <MenuItem
              onClick={() => {
                setContextMenu();
                if (openLogs) {
                  openLogs(rightClickedRow);
                }
              }}
            >
              <Stack direction={'row'} gap={1}>
                <ContentPasteGoRounded /> <span>Logs</span>
              </Stack>
            </MenuItem>
          </>
        )}

      </div>
    </>
  );
}
